import { useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from 'urql';

import {
  type CompositionV2Filters,
  PAY_V2_QUERY,
  type PayV2Req,
  PositionsEntityKind,
} from '@revelio/data-access';

import type { SalaryToggles } from '../IndividualPotentialPage';
import {
  buildCustomRoleFilter,
  type ResolvedProfileIds,
  type RoleLevel,
} from './useResolveProfileIds';

export type PayDataStatus = 'checking' | 'available' | 'no_data';

/** Maximum entities per PayV2 request */
const BATCH_SIZE = 10;

/** Similarity threshold — histograms this similar or more are considered identical */
const SIMILARITY_THRESHOLD = 0.9;

/**
 * Pick the best role level for pay data checks.
 * Same logic as overlay queries: prefer k150 for broadest useful signal.
 */
const pickCheckRoleLevel = (roleLevels: RoleLevel[]): RoleLevel | null =>
  roleLevels.find((r) => r.type === 'role_k150') ??
  roleLevels.find((r) => r.type === 'role_k1500') ??
  roleLevels.find((r) => r.type === 'role_k7') ??
  roleLevels.find((r) => r.type === 'role_k15000') ??
  null;

/** Build filters for pay data availability checks (same as overlay queries). */
const buildCheckFilters = (
  toggles: SalaryToggles,
  roleLevel: RoleLevel | null,
  seniorityId: number | null
): CompositionV2Filters => {
  const filters: CompositionV2Filters = { raw: false };

  if (toggles.role && roleLevel) {
    filters.custom_role = buildCustomRoleFilter(roleLevel);
  }

  if (toggles.seniority && seniorityId) {
    filters.seniority = [seniorityId];
  }

  // No geo — same as overlay queries
  return filters;
};

/** Split an array into chunks of a given size. */
const chunk = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

/**
 * Compute cosine similarity between two number arrays.
 * Returns a value between 0 (completely different) and 1 (identical direction).
 * Arrays must be the same length.
 */
const cosineSimilarity = (a: number[], b: number[]): number => {
  if (a.length !== b.length || a.length === 0) return 0;

  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  for (let i = 0; i < a.length; i++) {
    const aVal = a[i] ?? 0;
    const bVal = b[i] ?? 0;
    dotProduct += aVal * bVal;
    normA += aVal * aVal;
    normB += bVal * bVal;
  }

  const denominator = Math.sqrt(normA) * Math.sqrt(normB);
  if (denominator === 0) return 0;

  return dotProduct / denominator;
};

/**
 * Check whether a candidate histogram is too similar to the primary.
 * Uses cosine similarity — if ≥ SIMILARITY_THRESHOLD, the company's
 * data is considered to be the same generic fallback distribution.
 */
const isTooSimilar = (primary: number[], candidate: number[]): boolean => {
  const similarity = cosineSimilarity(primary, candidate);
  return similarity >= SIMILARITY_THRESHOLD;
};

/**
 * Pre-validates pay data availability for candidate overlay companies.
 *
 * 1. Fetches the primary company's histogram using the same overlay query
 *    structure (company-as-entity, role-k150 filter, seniority, no geo).
 * 2. Batches candidate companies into multi-entity PayV2 queries (up to 10
 *    entities per request) to efficiently fetch their histograms.
 * 3. Compares each candidate's histogram to the primary using cosine
 *    similarity. Companies with ≥90% similarity are marked as having no
 *    unique pay data (the API is returning a generic distribution).
 *
 * Returns a Map<rcid, PayDataStatus> that the UI uses to disable chips.
 */
export const usePayDataAvailability = (
  candidateRcids: number[],
  resolvedIds: ResolvedProfileIds,
  toggles: SalaryToggles
): {
  payDataStatus: Map<number, PayDataStatus>;
  isChecking: boolean;
} => {
  const gqlClient = useClient();
  const [payDataStatus, setPayDataStatus] = useState<
    Map<number, PayDataStatus>
  >(new Map());
  const [isChecking, setIsChecking] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const checkRole = useMemo(
    () => pickCheckRoleLevel(resolvedIds.roleLevels),
    [resolvedIds.roleLevels]
  );

  // Stable dependency keys
  const candidateIdsKey = useMemo(
    () => candidateRcids.join(','),
    [candidateRcids]
  );
  const roleKey = checkRole ? `${checkRole.type}-${checkRole.id}` : '';
  const seniorityKey = String(resolvedIds.seniorityId ?? '');
  const toggleRoleOn = toggles.role;
  const toggleSeniorityOn = toggles.seniority;
  const primaryRcid = resolvedIds.companyRcid;

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (candidateRcids.length === 0 || !primaryRcid) {
      setPayDataStatus(new Map());
      setIsChecking(false);
      return;
    }

    let cancelled = false;
    setIsChecking(true);

    // Immediately mark all candidates as 'checking'
    const initialStatus = new Map<number, PayDataStatus>();
    for (const rcid of candidateRcids) {
      initialStatus.set(rcid, 'checking');
    }
    setPayDataStatus(initialStatus);

    const run = async () => {
      try {
        const filters = buildCheckFilters(
          toggles,
          checkRole,
          resolvedIds.seniorityId
        );

        // Step 1: Fetch the primary company's histogram
        const primaryReq: PayV2Req = {
          entities: [
            { EntID: primaryRcid, EntType: PositionsEntityKind.Company },
          ],
          filters,
        };

        const primaryResult = await gqlClient
          .query(PAY_V2_QUERY, { req: primaryReq })
          .toPromise();

        if (cancelled || !mountedRef.current) return;

        const primaryHistogram =
          primaryResult.data?.pay_v2?.entities?.[0]?.metrics?.histogram?.base;

        if (!primaryHistogram || primaryHistogram.length === 0) {
          // Can't compare without primary data — mark all as available
          if (!cancelled && mountedRef.current) {
            const status = new Map<number, PayDataStatus>();
            for (const rcid of candidateRcids) {
              status.set(rcid, 'available');
            }
            setPayDataStatus(status);
            setIsChecking(false);
          }
          return;
        }

        // Step 2: Batch candidate companies into multi-entity queries
        const candidatesExcludingPrimary = candidateRcids.filter(
          (rcid) => rcid !== primaryRcid
        );
        const batches = chunk(candidatesExcludingPrimary, BATCH_SIZE);
        const statusMap = new Map<number, PayDataStatus>();

        const processBatch = async (batchRcids: number[]): Promise<void> => {
          const req: PayV2Req = {
            entities: batchRcids.map((rcid) => ({
              EntID: rcid,
              EntType: PositionsEntityKind.Company,
            })),
            filters,
          };

          const result = await gqlClient
            .query(PAY_V2_QUERY, { req })
            .toPromise();

          if (cancelled) return;

          const entities = result.data?.pay_v2?.entities;
          if (!entities) {
            // If the entire batch failed, mark all as available
            for (const rcid of batchRcids) {
              statusMap.set(rcid, 'available');
            }
            return;
          }

          // Build a lookup from entity ID → histogram
          const histogramByRcid = new Map<number, number[]>();
          for (const entity of entities) {
            if (!entity) continue;
            const entityId = entity.metadata?.id;
            const histogram = entity.metrics?.histogram?.base;
            if (entityId && histogram && histogram.length > 0) {
              histogramByRcid.set(entityId, histogram as number[]);
            }
          }

          // Compare each candidate's histogram to the primary
          for (const rcid of batchRcids) {
            const histogram = histogramByRcid.get(rcid);
            if (!histogram) {
              statusMap.set(rcid, 'no_data');
              continue;
            }

            const tooSimilar = isTooSimilar(
              primaryHistogram as number[],
              histogram
            );
            statusMap.set(rcid, tooSimilar ? 'no_data' : 'available');
          }
        };

        for (const batch of batches) {
          if (cancelled) return;
          await processBatch(batch);
        }

        if (!cancelled && mountedRef.current) {
          setPayDataStatus(statusMap);
        }
      } catch {
        // On error, mark all as available (don't block the user)
        if (!cancelled && mountedRef.current) {
          const status = new Map<number, PayDataStatus>();
          for (const rcid of candidateRcids) {
            status.set(rcid, 'available');
          }
          setPayDataStatus(status);
        }
      } finally {
        if (!cancelled && mountedRef.current) {
          setIsChecking(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    candidateIdsKey,
    roleKey,
    seniorityKey,
    toggleRoleOn,
    toggleSeniorityOn,
    primaryRcid,
    gqlClient,
  ]);

  return { payDataStatus, isChecking };
};
